import { defineMessages, useIntl } from "react-intl";

import {
    PriceFinderMessages,
    ProductPlan
} from "@/components/wpl/pricing/src/price-finder/constants";
import PriceFinderSection from "@/components/wpl/pricing/src/price-finder/price-finder";

import { JiraServiceManagementPricingPageProps } from "../../../../pages/software/jira/service-management/pricing/jira-service-management-pricing-page";
import {
    AdminControls,
    IdentityAccessManagement,
    UnlimitedAutomations,
    MultipleInstances,
    AtlassianAnalytics,
    AllIssuesSupport
} from "../expander-contents/enterprise";
import {
    AtlassianCommunity,
    ChangeManagement,
    IncidentProblemManagement,
    KnowledgeBase,
    MultiChannelSupport,
    Templates,
    WorkIntake
} from "../expander-contents/free";
import {
    AlertIntegrationIncidentInvestigation,
    AssetConfigurationManagement,
    AtlassianIntelligence,
    CriticalIssueSupport,
    DeploymentGating,
    IncidentMonitoring,
    VirtualAgent
} from "../expander-contents/premium";
import {
    AuditLogsDataResidency,
    HelpCenter,
    IncidentEscalation,
    RegionalSupport,
    UnlimitedCustomers,
    UnlimitedEmailNotifications
} from "../expander-contents/standard";

const JiraServiceManagementPriceFinderTeam24 = (
    props: JiraServiceManagementPricingPageProps
) => {
    const intl = useIntl();

    const freePlanFeatureMessages = [
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "JiraServiceManagementPricingPageTeam24.priceFinder.templates",
                    defaultMessage:
                        "Templates for ITSM, Customer Service, HR, and more"
                }),
                content: Templates
            }
        },
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "JiraServiceManagementPricingPageTeam24.priceFinder.multiChannelSupport",
                    defaultMessage:
                        "Multi-channel support, including customer portal, email, and chat"
                }),
                content: MultiChannelSupport
            }
        },
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "JiraServiceManagementPricingPageTeam24.priceFinder.workIntake",
                    defaultMessage:
                        "Work intake through customizable forms, workflows, and queues"
                }),
                content: WorkIntake
            }
        },
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "JiraServiceManagementPricingPageTeam24.priceFinder.knowledgeBase",
                    defaultMessage: "Embedded knowledge base"
                }),
                content: KnowledgeBase
            }
        },
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "JiraServiceManagementPricingPageTeam24.priceFinder.incidentProblemManagement",
                    defaultMessage: "Incident and problem management"
                }),
                content: IncidentProblemManagement
            }
        },
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "JiraServiceManagementPricingPageTeam24.priceFinder.changeManagement",
                    defaultMessage: "Change management"
                }),
                content: ChangeManagement
            }
        },
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "JiraServiceManagementPricingPageTeam24.priceFinder.atlassianCommunity",
                    defaultMessage: "Support from Atlassian Community"
                }),
                content: AtlassianCommunity
            }
        }
    ];
    const standardPlanFeatureMessages = [
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "JiraServiceManagementPricingPageTeam24.priceFinder.helpCenter",
                    defaultMessage: "Custom-branded help center"
                }),
                content: HelpCenter
            }
        },
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "JiraServiceManagementPricingPageTeam24.priceFinder.incidentEscalation",
                    defaultMessage:
                        "Alerts, on-call schedules, and incident escalation"
                }),
                content: IncidentEscalation
            }
        },
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "JiraServiceManagementPricingPageTeam24.priceFinder.unlimitedEmailNotifications",
                    defaultMessage: "Unlimited email notifications"
                }),
                content: UnlimitedEmailNotifications
            }
        },
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "JiraServiceManagementPricingPageTeam24.priceFinder.auditLogsDataResidency",
                    defaultMessage: "Audit logs and multi-region data residency"
                }),
                content: AuditLogsDataResidency
            }
        },
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "JiraServiceManagementPricingPageTeam24.priceFinder.unlimitedCustomers",
                    defaultMessage:
                        "Up to 20,000 agents and unlimited customers"
                }),
                content: UnlimitedCustomers
            }
        },
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "JiraServiceManagementPricingPageTeam24.priceFinder.regionalSupport",
                    defaultMessage: "9/5 regional support"
                }),
                content: RegionalSupport
            }
        }
    ];

    const premiumPlanFeatureMessages = [
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "JiraServiceManagementPricingPageTeam24.priceFinder.aiPoweredService",
                    defaultMessage: "AI-powered service and operations"
                }),
                content: AtlassianIntelligence
            }
        },
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "JiraServiceManagementPricingPageTeam24.priceFinder.virtualAgent",
                    defaultMessage: "Virtual agent"
                }),
                content: VirtualAgent
            }
        },
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "JiraServiceManagementPricingPageTeam24.priceFinder.assetConfigurationManagement",
                    defaultMessage: "Asset and configuration management"
                }),
                content: AssetConfigurationManagement
            }
        },
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "JiraServiceManagementPricingPageTeam24.priceFinder.deploymentGating",
                    defaultMessage: "Deployment gating with CI/CD tools"
                }),
                content: DeploymentGating
            }
        },
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "JiraServiceManagementPricingPageTeam24.priceFinder.alertIntegrationIncidentInvestigation",
                    defaultMessage:
                        "Advanced alert integrations and incident investigation"
                }),
                content: AlertIntegrationIncidentInvestigation
            }
        },
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "JiraServiceManagementPricingPageTeam24.priceFinder.incidentMonitoring",
                    defaultMessage: "Real-time incident monitoring"
                }),
                content: IncidentMonitoring
            }
        },
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "JiraServiceManagementPricingPageTeam24.priceFinder.criticalIssueSupport",
                    defaultMessage: "24/7 support for critical issues"
                }),
                content: CriticalIssueSupport
            }
        },
        {
            featureMessage: intl.formatMessage({
                id: "JiraServiceManagementPricingPageTeam24.priceFinder.premiumUptimeSLA",
                defaultMessage: "99.9% uptime SLA"
            })
        }
    ];

    const enterprisePlanFeatureMessages = [
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "JiraServiceManagementPricingPageTeam24.priceFinder.atlassianIntelligence",
                    defaultMessage:
                        "Cross-product insights with Atlassian Analytics and Data Lake"
                }),
                content: AtlassianAnalytics
            }
        },
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "JiraServiceManagementPricingPageTeam24.priceFinder.adminControls",
                    defaultMessage: "Advanced admin controls and security"
                }),
                content: AdminControls
            }
        },
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "JiraServiceManagementPricingPageTeam24.priceFinder.identityAccessManagement",
                    defaultMessage:
                        "Enterprise-grade identity and access management"
                }),
                content: IdentityAccessManagement
            }
        },
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "JiraServiceManagementPricingPageTeam24.priceFinder.unlimitedAutomations",
                    defaultMessage: "Unlimited automations"
                }),
                content: UnlimitedAutomations
            }
        },
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "JiraServiceManagementPricingPageTeam24.priceFinder.multipleInstances",
                    defaultMessage: "Multiple instances (up to 150)"
                }),
                content: MultipleInstances
            }
        },
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "JiraServiceManagementPricingPageTeam24.priceFinder.allIssuesSupport",
                    defaultMessage: "24/7 support for all issues"
                }),
                content: AllIssuesSupport
            }
        },
        {
            featureMessage: intl.formatMessage({
                id: "JiraServiceManagementPricingPageTeam24.priceFinder.enterpriseUptimeSLA",
                defaultMessage: "99.95% uptime SLA"
            })
        }
    ];

    // planNames should not be translated

    // Note: We need to use the jiraServiceDesk plan name instead of a new jiraServiceManagement key
    // because of how the pricing data is stored in CCP/HAMS. jiraServiceManagement is used as the dataProductKey
    // in all other components on the JSM pricing page.
    const jiraServiceManagementProductPlans: ProductPlan<"jiraServiceDesk">[] =
        [
            {
                planName: "Free",
                offeringSku: "jira-servicedesk-free",
                description: intl.formatMessage({
                    id: "JiraServiceManagementPricingPageTeam24.priceFinder.freeDescription",
                    defaultMessage: "Free forever for 3 agents"
                }),
                recommendationLimit: 0,
                signupLimit: 20000,
                signupURLSuffix:
                    "?bundle=jira-service-management&edition=free&signupSource=pricing",
                planFeatureMessages: freePlanFeatureMessages
            },
            {
                planName: "Standard",
                offeringSku: "jira-servicedesk-standard",
                description: intl.formatMessage({
                    id: "JiraServiceManagementPricingPageTeam24.priceFinder.standardDescription",
                    defaultMessage: "Everything you need to get started"
                }),
                recommendationLimit: 3,
                signupLimit: 20000,
                signupURLSuffix:
                    "?bundle=jira-service-management&edition=standard",
                planFeatureMessages: standardPlanFeatureMessages
            },
            {
                planName: "Premium",
                offeringSku: "jira-servicedesk-premium",
                description: intl.formatMessage({
                    id: "JiraServiceManagementPricingPageTeam24.priceFinder.premiumDescription",
                    defaultMessage: "Scale your service management"
                }),
                recommendationLimit: 50,
                signupLimit: 20000,
                signupURLSuffix:
                    "?bundle=jira-service-management&edition=premium",
                planFeatureMessages: premiumPlanFeatureMessages
            },
            {
                planName: "Enterprise",
                recommendationLimit: 250,
                description: intl.formatMessage({
                    id: "JiraServiceManagementPricingPageTeam24.priceFinder.enterpriseDescription",
                    defaultMessage:
                        "Advanced analytics, scale, and security for enterprises"
                }),
                signupLimit: 20000,
                planFeatureMessages: enterprisePlanFeatureMessages
            }
        ];

    const messages: PriceFinderMessages = defineMessages({
        headerText: {
            id: "JiraServiceManagementPricingPageTeam24.priceFinder.headerText",
            defaultMessage: "Service management <blue>that scales</blue>"
        },
        unitCountInputTextRedesign: {
            id: "JiraServiceManagementPricingPage.priceFinder.unitCountInputTextRedesign",
            defaultMessage: "Team size:"
        },
        unitCountInputTerm: {
            id: "JiraServiceManagementPricingPage.priceFinder.unitCountInputTerm",
            defaultMessage: "agents"
        },
        billingCycleText: {
            id: "JiraServiceManagementPricingPage.priceFinder.billingCycleText",
            defaultMessage: "Billing cycle:"
        },
        unitCountInputText: {
            id: "JiraServiceManagementPricingPage.priceFinder.unitCountInputText",
            defaultMessage: "How many agents are on your team?"
        },
        unitCountInputTooltip: {
            id: "JiraServiceManagementPricingPage.priceFinder.unitCountInputTooltip",
            defaultMessage:
                "Agents work on tickets and communicate directly with your customers."
        },
        monthlyExampleHelpText: {
            id: "JiraServiceManagementPricingPage.priceFinder.monthlyExampleHelpText",
            defaultMessage:
                "Monthly plans give you flexibility as your number of active agents changes, so pay for seats as you use them. Remember that the number of seats may change the total price."
        },
        annualExampleHelpText: {
            id: "JiraServiceManagementPricingPage.priceFinder.annualExampleHelpText",
            defaultMessage:
                "Annual plans give you flexibility and help you prepare for growth. Buy a group of seats at a flat rate now, then fill your empty seats through the year at no extra cost."
        },
        exampleStandardPlan: {
            id: "JiraServiceManagementPricingPage.priceFinder.exampleStandardPlan",
            defaultMessage: "Pricing for {unitCount} agents on Standard plan",
            description: "Standard is a plan name and should not be translated"
        },
        monthlyExampleUnitCountText: {
            id: "JiraServiceManagementPricingPage.priceFinder.monthlyExampleUserCount",
            defaultMessage: "Agent count"
        },
        monthlyExampleUnitCountAt: {
            id: "JiraServiceManagementPricingPage.priceFinder.monthlyExampleUnitCountAt",
            defaultMessage: "agents @",
            description: "will be used as follows: 100 agent @ $8.15"
        },
        monthlyExampleUnitCountTotal: {
            id: "JiraServiceManagementPricingPage.priceFinder.monthlyExampleUnitCount",
            defaultMessage: "{unitCount} agents"
        },
        monthlyExampleEstimatedPrice: {
            id: "JiraServiceManagementPricingPage.priceFinder.monthlyExampleEstimatedPrice",
            defaultMessage:
                "Estimated price per agent: <strong>{price}</strong>"
        },
        annualExampleUnitCountTierText: {
            id: "JiraServiceManagementPricingPage.priceFinder.annualExampleUnitCountTierText",
            defaultMessage: "Agent tier"
        },
        annualExampleEstimatedPrice: {
            id: "JiraServiceManagementPricingPage.priceFinder.annualExampleEstimatedPrice",
            defaultMessage:
                "Estimated price per agent/per month: <strong>{price}</strong>"
        },
        cardOverSignupLimit: {
            id: "JiraServiceManagementPricingPage.priceFinder.cardOverSignupLimit",
            defaultMessage: "Your agent count exceeds this plan's limit"
        },
        cardEnterpriseMinimumCount: {
            id: "JiraServiceManagementPricingPage.priceFinder.cardEnterpriseMinimumCount",
            defaultMessage:
                "Enterprise plan pricing is available when you enter {enterpriseMinimumCount} or more agents above",
            description:
                "Enterprise is a plan name and should not be translated"
        },
        cardUnitCountTierRange: {
            id: "JiraServiceManagementPricingPage.priceFinder.cardUnitCountTierRange",
            defaultMessage: "Agent tier: {lowerLimit} - {upperLimit}"
        },
        cardMonthlyPerUnitCount: {
            id: "JiraServiceManagementPricingPageTeam24.priceFinder.cardMonthlyPerUnitCount",
            defaultMessage: "per agent / month"
        },
        cardFreeDescription: {
            id: "JiraServiceManagementPricingPage.priceFinder.cardFreeDescription",
            defaultMessage: "always free for 3 agents,"
        },
        freeLimitModalTitle: {
            id: "JiraServiceManagementPricingPage.priceFinder.freeLimitModalTitle",
            defaultMessage: "Free has a {freeLimitCount}-user limit",
            description: "Free is a plan name and should not be translated"
        },
        freeLimitModalMessage: {
            id: "JiraServiceManagementPricingPage.priceFinder.freeLimitModalMessage",
            defaultMessage:
                "If you'd like to add more than {freeLimitCount} agents, consider another plan instead."
        }
    });

    return (
        <PriceFinderSection
            productPlans={jiraServiceManagementProductPlans}
            hamsProductKey="jira-servicedesk.ondemand"
            messages={messages}
            serverSideCCPPricing={
                props.pricing?.ccpPricingData?.jiraServiceDesk
            }
            serverSideHAMSPricing={
                props.pricing?.hamsPricingData?.jiraServiceDesk
            }
            countryCode={props.countryCode}
            dataProductKey="jiraServiceDesk"
            enterpriseMinimumCount={201}
            defaultUnitCount={75}
        />
    );
};

export default JiraServiceManagementPriceFinderTeam24;
